import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n/useI18n';
import LanguageSwitcher from '../components/LanguageSwitcher';
import API_CONFIG from '../config/apiConfig';

export default function Register() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (password !== confirmPassword) {
      setError(t('passwordMismatch'));
      return;
    }

    if (password.length < 6) {
      setError(t('passwordTooShort'));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_CONFIG.FULL_API_BASE_URL}/auth/register.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          nickname,
        }),
        credentials: 'include'
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(t('registerSuccess'));
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError(result.message || t('registerFailed'));
      }
    } catch (err) {
      setError(t('networkError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full" style={{ maxWidth: '400px' }}>
        <div className="text-center mb-6">
          {/* 注册图标 */}
          <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
            <span className="text-3xl">📝</span>
          </div>
          {/* 语言选择行：地球图标 + 语言切换器 */}
          <div className="flex items-center justify-center gap-1 mb-3">
            <span className="text-xl">🌐</span>
            <LanguageSwitcher variant="light" />
          </div>
          {/* 主标题 */}
          <h1 className="text-xl font-bold text-gray-800">{t('registerTitle')}</h1>
          <p className="text-gray-500 text-xs mt-1">{t('registerSubtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 text-green-600 p-3 rounded-lg text-sm">
              {success}
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-gray-400 mb-1">{t('email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder={t('enterEmail')}
              autoComplete="email"
              className="w-full box-border bg-gray-50 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-xl p-2 outline-none transition-all font-bold text-gray-800"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-400 mb-1">{t('nickname')}</label>
            <input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              required
              placeholder={t('enterNickname')}
              autoComplete="name"
              className="w-full box-border bg-gray-50 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-xl p-2 outline-none transition-all font-bold text-gray-800"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-400 mb-1">{t('password')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder={t('enterPassword')}
              autoComplete="new-password"
              className="w-full box-border bg-gray-50 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-xl p-2 outline-none transition-all font-bold text-gray-800"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-400 mb-1">{t('confirmPassword')}</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder={t('confirmPasswordPlaceholder')}
              autoComplete="new-password"
              className="w-full box-border bg-gray-50 border-2 border-transparent focus:border-indigo-500 focus:bg-white rounded-xl p-2 outline-none transition-all font-bold text-gray-800"
            />
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold shadow-lg shadow-indigo-100 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('registering') : t('registerButton')}
            </button>
          </div>
        </form>

        <div className="mt-6 text-center">
          <p className="text-gray-500 text-xs">
            {t('alreadyHaveAccount')}{' '}
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-indigo-600 font-bold hover:underline"
            >
              {t('loginHere')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}